/**
 * nodeReferenceCapabilities — derive which media-type tabs a node's reference
 * picker should show, from the generated YDoc field specs (single source of
 * truth from `schemas/node_defs.py`).
 *
 * Used by ReferenceSelector / ConfigTabContent / VideoEditorNode / VideoGeneratorNode
 * so the picker only offers tabs the target node can actually consume. Without
 * this, users could pick e.g. a video reference on an image_generator and then
 * fail to "Save & Close" because the resulting edge has no matching input port.
 */
import { YDOC_NODE_FIELDS } from '../../../../generated/nodeDataTypes'
import type { MediaType } from './mentionUtils'
import { createNadDebug } from '../../../../utils/debugLog'

const log = createNadDebug('dw:reference-tabs')

const SCHEMA_TYPE_TO_MEDIA: Record<string, MediaType> = {
  image: 'image',
  video: 'video',
  audio: 'audio',
}

const MEDIA_ORDER: readonly MediaType[] = ['image', 'video', 'audio']

/**
 * Return the media types the given backend node type accepts as a reference
 * input, derived from input ports whose `schemaType` is one of image/video/audio.
 *
 * Throws if `backendType` is not a known node type — per the schema-driven
 * invariant "Generated lookups must succeed".
 */
export function getAcceptedReferenceMediaTypes(backendType: string): MediaType[] {
  const fields = YDOC_NODE_FIELDS[backendType]
  if (!fields) {
    throw new Error(
      `[nodeReferenceCapabilities] unknown backendType "${backendType}" — not in YDOC_NODE_FIELDS`,
    )
  }
  const accepted = new Set<MediaType>()
  for (const [fieldName, spec] of Object.entries(fields)) {
    if (!fieldName.startsWith('in_')) continue
    if (spec.hiddenFromReferencePicker) continue
    for (const type of spec.schemaType.split('|')) {
      const media = SCHEMA_TYPE_TO_MEDIA[type]
      if (media) accepted.add(media)
    }
  }
  const result = MEDIA_ORDER.filter((m) => accepted.has(m))
  log('derive-tabs %o', { backendType, mediaTypes: result })
  return result
}
